/**
 * Game State Manager
 * Handles transitions between menu and game states
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import { Engine } from '../engine/Engine';

export type GameState = 'menu' | 'loading' | 'playing' | 'paused' | 'results';

export type GameMode = 'solo' | 'friends' | 'matchmaking';

export interface GameConfig {
  mode: GameMode;
  /** Actual game type chosen at match-time. Drives scene + state-machine
   *  selection. `mode` only describes the matchmaking flow (solo/friends/
   *  matchmaking); `gameType` describes the rules. */
  gameType?: 'ceelo' | 'craps' | 'solo';
  diceCount: number;
  betAmount?: number;
}

export class GameStateManager extends EventEmitter {
  private engine: Engine;
  private currentState: GameState = 'menu';
  private currentConfig: GameConfig | null = null;

  constructor(engine: Engine) {
    super();
    this.engine = engine;
  }

  /**
   * Get current game state
   */
  getState(): GameState {
    return this.currentState;
  }

  /**
   * Get current game config
   */
  getConfig(): GameConfig | null {
    return this.currentConfig;
  }

  /**
   * Start a game with the given configuration
   */
  startGame(config: GameConfig): void {
    if (this.currentState === 'playing') {
      console.warn('Game already in progress');
      return;
    }

    this.currentConfig = config;
    this.setState('loading');

    // Brief loading state, then start
    setTimeout(() => {
      this.setState('playing');
      this.engine.start();
      this.emit('game:started', { config });
    }, 100);
  }

  /**
   * End the current game and return to menu
   */
  endGame(): void {
    // Any non-menu state can end (playing/paused/loading/results) — the menu
    // Exit button must always stop the engine loop, whatever beat we're in.
    if (this.currentState === 'menu') {
      return;
    }

    this.engine.stop();
    this.currentConfig = null;
    this.setState('menu');
    this.emit('game:ended', {});
  }

  /**
   * Pause the game
   */
  pauseGame(): void {
    if (this.currentState !== 'playing') return;

    this.setState('paused');
    // Engine keeps running for rendering, but game logic pauses
    this.emit('game:paused', {});
  }

  /**
   * Resume the game
   */
  resumeGame(): void {
    if (this.currentState !== 'paused') return;

    this.setState('playing');
    this.emit('game:resumed', {});
  }

  /**
   * Show results (after dice stop)
   */
  showResults(results: { values: number[]; total: number }): void {
    if (this.currentState !== 'playing') return;

    this.setState('results');
    this.emit('game:results', results);
  }

  /**
   * Continue from results (throw again or exit)
   */
  continueFromResults(): void {
    if (this.currentState !== 'results') return;

    this.setState('playing');
    this.emit('game:continue', {});
  }

  private setState(newState: GameState): void {
    const oldState = this.currentState;
    this.currentState = newState;
    this.emit('state:changed', { from: oldState, to: newState });
  }
}
